import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

const TOOLTIP_ID = "manage--sales-chart-tooltip"

const formatPrice = (value) => `$${(Number(value) || 0).toLocaleString("es-CL")}`

const buildTooltipItem = ({ label, value, color }) => {
  const item = document.createElement("div")
  item.style.borderLeftWidth = "4px"
  item.style.borderLeftStyle = "solid"
  item.style.borderLeftColor = color
  item.style.paddingLeft = "8px"

  const name = document.createElement("div")
  name.style.fontSize = "14px"
  name.style.marginBottom = "4px"
  name.style.color = "#000"
  name.textContent = label

  const amount = document.createElement("div")
  amount.style.fontSize = "16px"
  amount.style.fontWeight = "500"
  amount.style.color = "#000"
  amount.textContent = value

  item.appendChild(name)
  item.appendChild(amount)
  return item
}

const ensureTooltipEl = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector(`#${TOOLTIP_ID}`)
  if (tooltipEl) return tooltipEl

  tooltipEl = document.createElement("div")
  tooltipEl.id = TOOLTIP_ID
  Object.assign(tooltipEl.style, {
    position: "absolute",
    background: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
    padding: "16px",
    pointerEvents: "none",
    opacity: "0",
    transition: "opacity 0.15s ease",
    zIndex: "10",
    minWidth: "180px",
  })
  chart.canvas.parentNode.appendChild(tooltipEl)
  return tooltipEl
}

export default class extends Controller {
  static values = {
    data: Object,
    totalLabel: { type: String, default: "Ingresos totales" },
  }

  connect() {
    const ctx = this.element.getContext("2d")
    const data = this.dataValue
    const totalLabel = this.totalLabelValue

    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: "Subscripciones existentes",
            data: data.renewed,
            backgroundColor: "#ffc05f",
            borderRadius: 2,
          },
          {
            label: "Pago único",
            data: data.one_off,
            backgroundColor: "#2b7ff2",
            borderRadius: 2,
          },
          {
            label: "Nueva Subscripciones",
            data: data.signups,
            backgroundColor: "#6bd58b",
            borderRadius: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              color: "#7a7a7a",
              font: { size: 13, weight: 500 },
            },
            border: { display: false },
          },
          y: {
            stacked: true,
            position: "right",
            grid: { color: "#dfdfdf" },
            ticks: {
              color: "#7a7a7a",
              font: { size: 13, weight: 500 },
              callback: (value) => `$${value.toLocaleString("es-CL")}`,
            },
            border: { display: false },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (context) => this.renderTooltip(context, totalLabel),
          },
        },
      },
    })
  }

  renderTooltip(context, totalLabel) {
    const { chart, tooltip } = context
    const tooltipEl = ensureTooltipEl(chart)

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0"
      return
    }

    const title = (tooltip.title && tooltip.title[0]) || ""
    const dataPoints = tooltip.dataPoints || []

    while (tooltipEl.firstChild) tooltipEl.removeChild(tooltipEl.firstChild)

    const titleEl = document.createElement("div")
    Object.assign(titleEl.style, {
      fontWeight: "700",
      fontSize: "14px",
      marginBottom: "10px",
      color: "#000",
    })
    titleEl.textContent = title
    tooltipEl.appendChild(titleEl)

    const list = document.createElement("div")
    list.style.display = "flex"
    list.style.flexDirection = "column"
    list.style.gap = "6px"

    const reversed = dataPoints.slice().reverse()
    for (const point of reversed) {
      list.appendChild(
        buildTooltipItem({
          label: point.dataset.label,
          value: formatPrice(point.raw),
          color: point.dataset.backgroundColor,
        })
      )
    }

    const total = dataPoints.reduce((sum, point) => sum + (Number(point.raw) || 0), 0)
    list.appendChild(
      buildTooltipItem({
        label: totalLabel,
        value: formatPrice(total),
        color: "#000",
      })
    )

    tooltipEl.appendChild(list)

    const { offsetLeft, offsetTop } = chart.canvas
    const containerWidth = chart.canvas.parentNode.clientWidth
    const tooltipWidth = tooltipEl.offsetWidth

    let left = offsetLeft + tooltip.caretX + 12
    if (left + tooltipWidth > containerWidth) {
      left = offsetLeft + tooltip.caretX - tooltipWidth - 12
    }
    const top = offsetTop + tooltip.caretY - tooltipEl.offsetHeight / 2

    tooltipEl.style.left = `${Math.max(0, left)}px`
    tooltipEl.style.top = `${Math.max(0, top)}px`
    tooltipEl.style.opacity = "1"
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    const tooltipEl = this.element.parentNode?.querySelector(`#${TOOLTIP_ID}`)
    if (tooltipEl) tooltipEl.remove()
  }
}
